import s from "./EvidenceChain.module.css";
import { useProvenance } from "../../api/queries";
import { KIND_LABEL, type Chain, type Link } from "../../domain/provenance";
import { ErrorState, Skeleton } from "../../design/components";
import { formatDate } from "../../lib/format";
import { t } from "../../i18n";

/**
 * Where this recommendation came from.
 *
 * An auditor does not ask what a number is — they ask where it came from, and
 * then where THAT came from, until they reach something a person saw or an
 * instrument recorded. This renders that walk for one candidate.
 *
 * THE GAPS ARE RENDERED, not omitted. A chain drawn only from the links that
 * exist looks short rather than broken, and hides the difference between
 * "nobody photographed this" and "this needed no photograph". A missing link
 * gets a row of its own, in the position it would have occupied.
 *
 * AND NOTHING IS SCORED. There is no percentage here, deliberately: a chain of
 * four observed links is not "92% reliable", and that number would be the
 * least defensible thing on the screen.
 */
export function EvidenceChain({ candidateId }: { candidateId: string }) {
  const chain = useProvenance(candidateId);

  if (chain.isPending) return <Skeleton rows={4} />;
  if (chain.isError) return <ErrorState error={chain.error} retry={() => chain.refetch()} />;
  if (!chain.data) return null;
  if (!chain.data.found) {
    return <div className={s.reading}>{chain.data.whyNot ?? t("No record for this candidate.")}</div>;
  }

  const c: Chain = chain.data;
  return (
    <div className={s.chain} data-testid={`chain-${candidateId}`}>
      <div className={`${s.reading} ${c.complete ? s.readingOk : s.readingGap}`}>{c.reading}</div>

      <ol className={s.links}>
        {c.links.map((l, i) => (
          <li key={`${l.step}-${i}`} className={`${s.link} ${s[l.kind]}`}>
            <div className={s.linkHead}>
              <span className={s.step}>{l.step}</span>
              <span className={s.kind}>{t(KIND_LABEL[l.kind])}</span>
            </div>
            <div className={s.what}>{l.what}</div>
            <Meta link={l} />
            {l.concern && <div className={s.concern}>{l.concern}</div>}
            {l.wouldStrengthen && (
              <div className={s.strengthen}>
                {t("Would strengthen it:")} {l.wouldStrengthen}
              </div>
            )}
          </li>
        ))}
      </ol>

      {c.noScore && <div className={s.noScore}>{c.noScore}</div>}
    </div>
  );
}

function Meta({ link }: { link: Link }) {
  const bits: string[] = [];
  if (link.source) bits.push(link.source);
  if (link.recordedBy) bits.push(link.recordedBy);
  if (link.recordedAt) {
    /* the age is the question an auditor asks, so it travels with the date
       rather than being left for them to subtract */
    bits.push(
      link.ageDays != null
        ? t("{date} — {n} days ago", { date: formatDate(link.recordedAt), n: link.ageDays })
        : formatDate(link.recordedAt),
    );
  }
  if (!bits.length) return null;
  return <div className={s.meta}>{bits.join(" · ")}</div>;
}
